<script setup lang="ts">
/**
 * The "View" panel for `sbi.viz.documents`: one whole document, read in place
 * — the photon's stored body (`assembly` `stored-…`) when it has a body
 * store, else rebuilt from the document's chunks (`stitched-…`). Only the
 * second gets the "Rebuilt from N passages" footer: it is the caveat that
 * paragraph breaks and headings are missing, which a stored body doesn't need.
 *
 * Fetches `GET /api/embed/insights/:id/documents/:doc_id?t=<view_token>`
 * with the embed token (see `http/embed/document-view.ts` — the view token
 * is what limits a viewer to documents a search showed them). Renders the
 * document's `markdown` without its YAML front matter (the header shows
 * title and byline instead), through markdown-it with HTML off and then
 * DOMPurify: the text is corpus content, so it is treated as untrusted.
 * "Copy markdown" copies photon's markdown exactly, front matter included.
 *
 * The document's declared fields show as "Label: value" badges under the
 * byline — one per field, a multi-valued field's values joined ("Topics:
 * Health, Wages") — labelled from the store's own descriptor (`field_info`), never
 * from a client's field names — skipping `id` and `internal` fields and
 * the `date` (already in the byline).
 */
import DOMPurify from "dompurify";
import MarkdownIt from "markdown-it";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId } from "vue";

import type { EmbedApi } from "../sbi/embed-context.ts";
import LucentSpinner from "./LucentSpinner.vue";
import { formatError } from "./errorText.ts";

/** The slice of photon's document body this panel reads. */
interface ViewedDocument {
  doc_id: string;
  title?: string | null;
  markdown?: string;
  chunk_count?: number;
  truncated?: boolean;
  /** How photon built the body: `stored-<version>` or `stitched-v1`. Absent from an older photon, which only stitched. */
  assembly?: string;
  fields?: Record<string, unknown>;
  field_info?: Array<{ name: string; label?: string | null; category?: string | null }>;
}

/** Values shown per field before "+N more". */
const BADGES_PER_FIELD = 8;
/** Field categories that aren't shown: opaque ids, plumbing, and the byline's date. */
const HIDDEN_CATEGORIES = new Set(["id", "internal", "date"]);

const props = defineProps<{
  api: EmbedApi;
  docId: string;
  viewToken: string;
  /** Shown while loading, and if the document has no title. */
  title?: string | null;
  /** The card's byline parts (source, author, date…). */
  byline?: string[];
  /** Noun for the document ("story"), for wording. */
  item?: string;
}>();

const emit = defineEmits<{ close: [] }>();

const md = new MarkdownIt({ html: false, linkify: true });
// Links in corpus text open outside the embed, never in it.
const defaultLink = md.renderer.rules.link_open ?? ((t, i, o, _e, self) => self.renderToken(t, i, o));
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  tokens[idx]!.attrSet("target", "_blank");
  tokens[idx]!.attrSet("rel", "noopener noreferrer");
  return defaultLink(tokens, idx, options, env, self);
};

const doc = ref<ViewedDocument | null>(null);
const failure = ref<string | null>(null);
const copied = ref(false);
const closeButton = ref<HTMLButtonElement | null>(null);
const dlg = ref<HTMLDialogElement | null>(null);
const titleId = useId();
const controller = new AbortController();

/** The markdown body: front matter and a leading `# title` dropped (the header has both). */
const body = computed(() => {
  let text = doc.value?.markdown ?? "";
  if (text.startsWith("---\n")) {
    const end = text.indexOf("\n---\n", 4);
    if (end !== -1) text = text.slice(end + 5);
  }
  return text.replace(/^\s*# [^\n]*\n/, "");
});

const rendered = computed(() =>
  DOMPurify.sanitize(md.render(body.value), { USE_PROFILES: { html: true }, ADD_ATTR: ["target"] }),
);

const heading = computed(() => doc.value?.title || props.title || "(untitled)");

const humanise = (name: string): string => {
  const words = name.replace(/_/g, " ").trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : name;
};

const shown = (v: unknown): string | null => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

/** One `{label, value}` badge per field, in the store's field order: a multi-valued field's values joined. */
const badges = computed(() => {
  const fields = doc.value?.fields ?? {};
  type Info = NonNullable<ViewedDocument["field_info"]>[number];
  const info: Info[] = doc.value?.field_info ?? Object.keys(fields).map((name) => ({ name }));
  const out: Array<{ key: string; label: string; value: string }> = [];
  for (const f of info) {
    if (f.category && HIDDEN_CATEGORIES.has(f.category)) continue;
    const label = f.label || humanise(f.name);
    const values = (Array.isArray(fields[f.name]) ? (fields[f.name] as unknown[]) : [fields[f.name]])
      .map(shown)
      .filter((v): v is string => v !== null);
    if (values.length === 0) continue;
    const extra = values.length - BADGES_PER_FIELD;
    const value = values.slice(0, BADGES_PER_FIELD).join(", ") + (extra > 0 ? ` +${extra} more` : "");
    out.push({ key: f.name, label, value });
  }
  return out;
});

const load = async () => {
  const base = props.api.apiUrl.replace(/\/$/, "");
  const url =
    `${base}/api/embed/insights/${encodeURIComponent(props.api.insightId)}` +
    `/documents/${encodeURIComponent(props.docId)}?t=${encodeURIComponent(props.viewToken)}`;
  try {
    const res = await fetch(url, {
      headers: { authorization: `Bearer ${props.api.token}` },
      signal: controller.signal,
    });
    const payload = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (!res.ok) {
      const message = typeof payload?.message === "string" ? payload.message : null;
      const exId = typeof payload?.exId === "string" ? payload.exId : undefined;
      failure.value =
        res.status === 403 && message ? message : formatError(`Couldn't open this ${props.item ?? "document"}`, exId);
      return;
    }
    doc.value = payload as unknown as ViewedDocument;
  } catch (e) {
    if (controller.signal.aborted) return;
    failure.value = formatError(`Couldn't open this ${props.item ?? "document"}`);
  }
};

const copy = async () => {
  if (!doc.value?.markdown) return;
  try {
    await navigator.clipboard.writeText(doc.value.markdown);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1500);
  } catch {
    // No clipboard permission (e.g. an insecure embed page): nothing to do.
  }
};

// A native modal <dialog>: the top layer can't be trapped by a host-page
// ancestor with `transform` / `filter` / `contain` the way a
// `position: fixed` overlay was, and Esc / focus containment are
// built in. Esc fires `cancel` → `close`; every way out funnels
// through the `close` event.
const close = () => {
  const d = dlg.value;
  if (d?.open && typeof d.close === "function") d.close();
  else emit("close");
};

// Clicks on the dialog box itself (it has no padding) are the backdrop.
const onDialogClick = (e: MouseEvent) => {
  if (e.target === dlg.value) close();
};

onMounted(() => {
  const d = dlg.value;
  if (d && !d.open && typeof d.showModal === "function") d.showModal();
  void nextTick(() => closeButton.value?.focus());
  void load();
});
onBeforeUnmount(() => {
  controller.abort();
});
</script>

<template>
  <dialog
    ref="dlg"
    class="viewer"
    :aria-labelledby="titleId"
    @close="emit('close')"
    @click="onDialogClick"
  >
    <div class="viewer-panel">
      <header class="viewer-head">
        <div class="viewer-heading">
          <h3 :id="titleId" class="viewer-title">{{ heading }}</h3>
          <div v-if="byline?.length" class="viewer-byline">
            <template v-for="(part, i) in byline" :key="i">
              <span v-if="i > 0" class="dot" aria-hidden="true">·</span><span>{{ part }}</span>
            </template>
          </div>
          <ul v-if="badges.length" class="viewer-badges" aria-label="Document details">
            <li v-for="b in badges" :key="b.key" class="field-badge">
              <span class="field-badge-label">{{ b.label }}:</span> {{ b.value }}
            </li>
          </ul>
        </div>
        <div class="viewer-actions">
          <button v-if="doc?.markdown" type="button" class="viewer-btn" @click="copy">
            {{ copied ? "Copied" : "Copy markdown" }}
          </button>
          <button ref="closeButton" type="button" class="viewer-btn viewer-close" aria-label="Close" @click="close">✕</button>
        </div>
      </header>

      <div class="viewer-body">
        <div v-if="failure" class="viewer-placeholder viewer-placeholder--error">{{ failure }}</div>
        <div v-else-if="!doc" class="viewer-placeholder"><LucentSpinner :size="32" /></div>
        <div v-else class="viewer-prose" v-html="rendered" />
      </div>

      <footer v-if="doc && !doc.assembly?.startsWith('stored-')" class="viewer-foot">
        Rebuilt from {{ doc.chunk_count ?? 0 }} {{ doc.chunk_count === 1 ? "passage" : "passages" }}
        <template v-if="doc.truncated"> · truncated</template>
      </footer>
    </div>
  </dialog>
</template>

<style scoped>
/* `display` only on the open state: scoped specificity would otherwise
   beat the UA's hidden rule for a closed dialog. */
.viewer {
  padding: 0; border: 0;
  background: var(--card, #fff);
  color: var(--lucent-chart-text-color, #2f2c26);
  border-radius: 12px;
  box-shadow: 0 18px 50px rgb(0 0 0 / 0.22);
  width: min(760px, calc(100vw - 32px));
  max-width: none;
  max-height: 86vh;
  max-height: 86dvh;
  overflow: hidden;
}
.viewer[open] { display: flex; flex-direction: column; }
.viewer::backdrop { background: rgb(20 18 14 / 0.38); }
.viewer-panel { display: flex; flex-direction: column; min-height: 0; flex: 1 1 auto; }
/* Phones: full screen. Viewport query on purpose — the dialog covers
   the screen, not the embed. */
@media (max-width: 639px) {
  .viewer {
    width: 100vw; height: 100%; max-height: 100%; margin: 0; border-radius: 0;
  }
  .viewer-head { padding-top: max(16px, env(safe-area-inset-top, 0px)); }
  .viewer-body { padding-bottom: max(18px, env(safe-area-inset-bottom, 0px)); }
}
@media (max-width: 479px) {
  .viewer-head { flex-wrap: wrap; }
}
.viewer-head {
  display: flex; gap: 12px; align-items: flex-start; justify-content: space-between;
  padding: 16px 18px 12px; border-bottom: 1px solid var(--rule-soft, #efede6);
}
.viewer-heading { flex: 1 1 auto; min-width: 0; }
.viewer-title { font: 600 17px/1.3 system-ui, sans-serif; letter-spacing: -0.01em; margin: 0; overflow-wrap: anywhere; }
.viewer-byline { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; font-size: 12px; color: var(--card-muted, #6a655b); }
.dot { color: #c9c3b4; }
.viewer-badges { display: flex; flex-wrap: wrap; gap: 5px; margin: 8px 0 0; padding: 0; list-style: none; }
.field-badge {
  font-size: 11px; line-height: 1.3; padding: 2px 8px; border-radius: 999px;
  border: 1px solid var(--rule, #e5e3dc); color: var(--lucent-chart-legend-color, #4a4641);
}
.field-badge-label { color: var(--card-muted, #6a655b); }
.viewer-actions { display: flex; gap: 6px; flex-shrink: 0; margin-left: auto; }
.viewer-btn {
  border: 1px solid var(--rule, #e5e3dc); background: var(--card, #fff); border-radius: 8px;
  padding: 5px 10px; font: 500 12px system-ui, sans-serif; color: var(--lucent-chart-legend-color, #4a4641); cursor: pointer;
  min-height: 32px; touch-action: manipulation;
}
@media (pointer: coarse) {
  .viewer-btn { min-height: 44px; min-width: 44px; }
}
.viewer-btn:focus-visible { box-shadow: 0 0 0 2px var(--lucent-focus, currentColor); outline: none; }
.viewer-close { padding: 5px 9px; }
.viewer-body { overflow-y: auto; overscroll-behavior: contain; min-height: 0; padding: 14px 18px 18px; }
.viewer-placeholder { display: flex; justify-content: center; padding: 32px 8px; font-size: 13px; color: var(--card-muted, #6a655b); }
.viewer-placeholder--error { color: var(--lucent-chart-negative, #d96363); }
.viewer-prose { font-size: 14px; line-height: 1.65; color: var(--lucent-chart-legend-color, #3c3934); overflow-wrap: anywhere; }
/* Corpus markdown can carry tables, code and images: scroll or scale
   them inside the column rather than scrolling the whole body sideways. */
.viewer-prose :deep(table) { display: block; max-width: 100%; overflow-x: auto; border-collapse: collapse; }
.viewer-prose :deep(th), .viewer-prose :deep(td) { padding: 4px 8px; border-bottom: 1px solid var(--rule-soft, #efede6); }
.viewer-prose :deep(pre) { max-width: 100%; overflow-x: auto; }
.viewer-prose :deep(pre code) { overflow-wrap: normal; }
.viewer-prose :deep(img) { max-width: 100%; height: auto; }
.viewer-prose :deep(p) { margin: 0 0 0.9em; }
.viewer-prose :deep(h1), .viewer-prose :deep(h2), .viewer-prose :deep(h3) { font-size: 15px; margin: 1.2em 0 0.5em; }
.viewer-prose :deep(a) { color: var(--lucent-link, #3b6fc0); }
.viewer-prose :deep(em) { color: var(--card-muted, #6a655b); }
.viewer-foot { padding: 9px 18px; border-top: 1px solid var(--rule-soft, #efede6); font-size: 11.5px; color: var(--card-muted, #6a655b); }
</style>
